import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Cog, LogOut, Menu, Search, ShieldCheck, UserCircle } from "lucide-react";

import { NotificationBell } from "@/components/NotificationBell";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useGlobalSearch } from "@/hooks/useSearch";
import { SearchResultsList } from "@/pages/Search";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useLogout } from "@/hooks/useLogout";
import { initials } from "@/lib/format";
import type { CurrentUser } from "@/lib/types";

const ROLE_VARIANT = {
  admin:    "destructive",
  ceo:      "default",
  director: "secondary",
  user:     "outline",
} as const;

interface TopbarProps {
  user: CurrentUser;
  /** Opens the sidebar drawer on mobile. The button itself is hidden
   *  at lg+ since the sidebar is always docked there. */
  onMobileMenuClick: () => void;
}

/**
 * Topbar — hamburger (mobile only), global search, notification bell,
 * role badge, user menu.
 */
export function Topbar({ user, onMobileMenuClick }: TopbarProps) {
  const navigate = useNavigate();
  const logout = useLogout();

  return (
    <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border/60 bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:gap-4 sm:px-6">
      {/* ----- Hamburger (mobile only) ----- */}
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 shrink-0 lg:hidden"
        onClick={onMobileMenuClick}
        aria-label="Open navigation"
      >
        <Menu className="h-5 w-5" />
      </Button>

      <GlobalSearchInput />

      <div className="ml-auto flex items-center gap-2">
        <Badge variant={ROLE_VARIANT[user.role]} className="hidden font-medium sm:inline-flex">
          <ShieldCheck className="mr-1 h-3 w-3" />
          {user.role.toUpperCase()}
        </Badge>

        <ThemeToggle />
        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-9 gap-2 px-1.5 hover:bg-accent/60"
            >
              <Avatar className="h-7 w-7 ring-2 ring-background">
                {user.avatar_url && <AvatarImage src={user.avatar_url} alt="" />}
                <AvatarFallback className="bg-gradient-to-br from-primary to-emerald-700 text-[11px] font-semibold text-primary-foreground">
                  {initials(user.full_name || user.email)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden flex-col leading-tight sm:flex sm:items-start">
                <span className="text-xs font-semibold">
                  {user.first_name || user.email}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {user.role}
                </span>
              </div>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="flex flex-col">
              <span className="font-semibold">{user.full_name || user.email}</span>
              <span className="text-xs font-normal text-muted-foreground">
                {user.email}
              </span>
              {user.department_name && (
                <span className="text-xs font-normal text-muted-foreground">
                  {user.department_name}
                </span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuItem asChild>
              <Link to="/profile">
                <UserCircle className="mr-2 h-4 w-4" />
                Profile &amp; settings
              </Link>
            </DropdownMenuItem>

            {user.is_admin_level && (
              <DropdownMenuItem onSelect={() => window.open("/admin/", "_blank")}>
                <Cog className="mr-2 h-4 w-4" />
                Django admin
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              disabled={logout.isPending}
              onSelect={(e) => {
                e.preventDefault();
                logout.mutate(undefined, {
                  onSuccess: () => navigate("/login", { replace: true }),
                });
              }}
            >
              <LogOut className="mr-2 h-4 w-4" />
              {logout.isPending ? "Signing out…" : "Sign out"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Global typeahead — hits /api/v1/search/ on every keystroke (cached) and
// drops a popover under the input with grouped results. Enter, or click
// "See all results" goes to /search?q=… for the full page.
// ---------------------------------------------------------------------------
function GlobalSearchInput() {
  const [q, setQ]       = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const wrapRef  = useRef<HTMLDivElement | null>(null);
  const search   = useGlobalSearch(q, { limit: 4 });

  // Close on click outside.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function gotoFullResults() {
    const term = q.trim();
    if (!term) return;
    navigate(`/search?q=${encodeURIComponent(term)}`);
    setOpen(false);
  }

  return (
    <div ref={wrapRef} className="relative hidden w-full max-w-sm sm:block">
      <form onSubmit={(e) => { e.preventDefault(); gotoFullResults(); }}>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search documents, articles, people…"
          className="h-9 pl-9 pr-3"
        />
      </form>

      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 overflow-hidden rounded-md border border-border/60 bg-popover shadow-md">
          <SearchResultsList
            hits={search.data?.results ?? []}
            loading={search.isLoading}
            onPick={(h) => { navigate(h.link); setOpen(false); setQ(""); }}
          />
          {(search.data?.results.length ?? 0) > 0 && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={gotoFullResults}
              className="block w-full border-t border-border/60 bg-muted/30 px-3 py-2 text-left text-xs font-medium text-primary transition hover:bg-accent/40"
            >
              See all results for "{q.trim()}" →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
